import React, { useEffect } from "react";
import { BasicIcon, Modal, Button, TimeInput, TextInput, NumericInput } from "@pickit/pickit-components";
import "./SlotsDetailModal.scss";
import { Field, Form, FormSpy } from "react-final-form";
import EnrolledDropdown from "./enrolledDropdown/EnrolledDropdownContainer";
import ConnectedDropdownContainer from "./conectedDropdown/ConnectedDropdownContainer";
import { SlotDetailValuesType, SlotsDetailModalProps } from "./types";
import { ISO8601toDDMMYYYHHMM } from "utils/date";
import { validationSchema, validationSchemaOnGoing } from "./schemas";
import useValidationSchema from "hooks/useValidationSchema";
import classNames from "classnames";
import { SlotsConstants } from "constants/slots";
import { useTranslation } from "react-i18next";
import "./i18n";

export const SlotsDetailModal: React.FC<SlotsDetailModalProps> = ({
  isOpen,
  loading,
  dropdownState,
  resolutionModal,
  slot,
  initialValues,
  newPickerDirty,
  pickerErrorCode,
  scrollContainerRef,
  dropdownContainerRef,
  formRef,
  testId,
  onClose,
  toggleDropdown,
  onSubmit,
  deleteSlotConfirm,
  setDirty,
  setInvalid,
  onRefresh,
  scroll,
}): JSX.Element => {
  const { t } = useTranslation();
  const dropdownContainerStyles = [
    "enroled-container",
    dropdownState === "closed" ? "enroled-container-hidden" : "",
  ].join(" ");

  useEffect(() => {
    if (!loading) {
      scroll();
    }
  }, [scroll, loading]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (isOpen) {
      params.set('modal', 'slot-detail');
    } else {
      params.delete('modal');
    }
    const paramsString = params.toString();
    const newUrl = paramsString ? `${window.location.pathname}?${paramsString}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, [isOpen]);
  
  return (
    <Modal
      width={"1190px"}
      height={`${resolutionModal}px`}
      isOpen={isOpen}
      onClose={onClose}
      testId={testId}
    >
      <div className="modal-container">
        <div className="container-modal-slot-row">
          <div className="container-modal-slot-col-sm-2">
            <h2 className="modal-slot-title" data-testId={`${testId}-title`}>
              {t("slotDetail:slotCode")}
            </h2>
            <p
              className="modal-slot-subtitle-code"
              data-testId={`${testId}-id`}
            >
              {slot.id}
            </p>
          </div>

          <div className="container-modal-slot-col-sm-2 ">
            <p
              className="modal-slot-state title"
              data-testId={`${testId}-state-title`}
            >
              {t("slotDetail:state")}
            </p>
            <p className="subtitle" data-testId={`${testId}-state`}>
              {slot.status.description}
            </p>
          </div>
          <div className="container-modal-slot-col-sm-1">
            <p
              className="modal-slot-state title"
              data-testId={`${testId}-expiration-title`}
            >
              {t("slotDetail:expiration")}
            </p>
            <p className="subtitle" data-testId={`${testId}-expiration`}>
              {slot.startAt && ISO8601toDDMMYYYHHMM(slot.finishAt)}
            </p>
          </div>
        </div>

        <hr className="modal-slot-separate" id="modal-slot-hr-title" />
        <Form
          onSubmit={(values: SlotDetailValuesType) => {}}
          initialValues={initialValues}
          validate={useValidationSchema(
            slot.status.tag === SlotsConstants.STATUS_IN_PROGRESS
              ? validationSchemaOnGoing(
                  initialValues.capacity,
                  initialValues.slotTime.end
                )
              : validationSchema
          )}
          mutators={{
            setValue: ([field], state) => {
              if (field === "slotTime.initial") {
                const f = state.fields["slotTime.end"];
                f.touched = true;
              }
            },
            forceValidation: ([field], state, { changeValue }) => {
              // change the value to the same value, thus triggering a revalidation
              changeValue(state, "capacity", (value) => value);
            },
          }}
        >
          {({ handleSubmit, form, dirty, invalid, values }) => {
            formRef.current = form;
            return (
              <form className="form-modal-slot" onSubmit={handleSubmit}>
                <FormSpy
                  subscription={{
                    dirty: true,
                    values: true,
                    invalid: true,
                  }}
                  onChange={(pro) => {
                    setInvalid(pro.invalid);
                    setDirty(pro.dirty);
                  }}
                />
                <div
                  className={`scroll-container ${
                    resolutionModal < 600 ? "small" : ""
                  } `}
                  ref={scrollContainerRef}
                >
                  <div className="container-modal-slot-form-row">
                    <div className="container-modal-slot-form-col-sm-1 container-modal-slot-form-col-col-lg-1">
                      <Field
                        name="slotTime.initial"
                        label={t("slotDetail:beginningLabel")}
                        component={TimeInput}
                        className="Admin-Pickers-input "
                        placeholder={t("slotDetail:beginningPlaceholder")}
                        testId={`${testId}-start-time`}
                        disabled={
                          slot.status.tag !== SlotsConstants.STATUS_CREATED
                        }
                        showClearIcon={slot.status.tag === SlotsConstants.STATUS_CREATED}
                      />
                    </div>
                    <div className="container-modal-slot-form-col-sm-1 container-modal-slot-form-col-col-lg-1">
                      <Field
                        name="slotTime.end"
                        label={t("slotDetail:endLabel")}
                        component={TimeInput}
                        className="Admin-Pickers-input "
                        placeholder={t("slotDetail:endPlaceholder")}
                        testId={`${testId}-end-time`}
                        disabled={
                          slot.status.tag !== SlotsConstants.STATUS_CREATED &&
                          slot.status.tag !== SlotsConstants.STATUS_IN_PROGRESS
                        }
                        showClearIcon={slot.status.tag === SlotsConstants.STATUS_CREATED || slot.status.tag === SlotsConstants.STATUS_IN_PROGRESS}
                      />
                    </div>
                    <div className="container-modal-slot-form-col-sm-1 container-modal-slot-form-col-col-lg-1">
                      <Field
                        type="text"
                        name="date"
                        label={t("slotDetail:date")}
                        component={TextInput}
                        className="Admin-Pickers-input"
                        placeholder=""
                        disabled
                        testId={`${testId}-date`}
                      />
                    </div>
                    <div className="container-modal-slot-form-col-sm-1 container-modal-slot-form-col-col-lg-1">
                      <Field
                        type="text"
                        name="region"
                        label={t("slotDetail:region")}
                        component={TextInput}
                        className="Admin-Pickers-input "
                        placeholder=""
                        disabled
                        testId={`${testId}-region`}
                      />
                    </div>
                  </div>
                  <div className="container-modal-slot-form-row">
                    <div className="container-modal-slot-form-col-sm-1 container-modal-slot-form-col-col-lg-1">
                      <Field
                        type="text"
                        name="workzone"
                        label={t("slotDetail:oprationZone")}
                        component={TextInput}
                        className="Admin-Pickers-input "
                        placeholder=""
                        disabled
                        testId={`${testId}-workzone`}
                      />
                    </div>
                    <div className="container-modal-slot-form-col-sm-1 container-modal-slot-form-col-col-lg-1">
                      <Field
                        type="text"
                        name="enroled"
                        label={t("slotDetail:registered")}
                        component={TextInput}
                        className="Admin-Pickers-input "
                        placeholder=""
                        disabled
                        testId={`${testId}-enrolled`}
                      />
                    </div>
                    <div className="container-modal-slot-form-col-sm-1 container-modal-slot-form-col-col-lg-1">
                      <Field
                        type="text"
                        name="connected"
                        label={t("slotDetail:connected")}
                        component={TextInput}
                        className="Admin-Pickers-input "
                        placeholder=""
                        disabled
                        testId={`${testId}-connected`}
                      />
                    </div>
                    <div className="container-modal-slot-form-col-sm-1 container-modal-slot-form-col-col-lg-1">
                      <Field
                        name="capacity"
                        label={t("slotDetail:capacityLabel")}
                        component={NumericInput}
                        maxInt={4}
                        allowDecimal={false}
                        className="Admin-Pickers-input "
                        placeholder={t("slotDetail:capacityPlaceholder")}
                        disabled={
                          slot.status.tag !==
                            SlotsConstants.STATUS_IN_PROGRESS &&
                          slot.status.tag !== SlotsConstants.STATUS_CREATED
                        }
                        testId={`${testId}-capacity`}
                      />
                    </div>
                  </div>
                  <hr
                    className="modal-slot-separate"
                    id="modal-slot-hr-title"
                  />
                  <div
                    className="dropdown-button-contianer"
                    ref={dropdownContainerRef}
                  >
                    <button
                      onClick={(e) => toggleDropdown("enrolled", e)}
                      type="button"
                      className={classNames("dropdown-button", {
                        active: dropdownState === "enrolled",
                        default: newPickerDirty,
                        disabled: dropdownState === "connected" && loading,
                      })}
                      data-testId={`${testId}-toggle-enrolled`}
                    >
                      {t("slotDetail:enrolled")}
                      <div className="chebron-container">
                        <BasicIcon
                          name={
                            dropdownState === "enrolled" && loading
                              ? "spinner"
                              : "chevron-down"
                          }
                          size="medium"
                        />
                      </div>
                    </button>

                    <button
                      onClick={(e) => toggleDropdown("connected", e)}
                      type="button"
                      className={classNames("dropdown-button", {
                        disabled:
                          newPickerDirty ||
                          (dropdownState === "enrolled" && loading),
                        active: dropdownState === "connected",
                      })}
                      data-testId={`${testId}-toggle-connected`}
                    >
                      {t("slotDetail:connected")}
                      <div className="chebron-container">
                        <BasicIcon
                          name={
                            dropdownState === "connected" && loading
                              ? "spinner"
                              : "chevron-down"
                          }
                          size="medium"
                        />
                      </div>
                    </button>
                  </div>
                  {!loading && (
                    <div className={dropdownContainerStyles}>
                      {dropdownState === "connected" ? (
                        <ConnectedDropdownContainer
                          testId={`${testId}-connected`}
                        />
                      ) : dropdownState === "enrolled" ? (
                        <EnrolledDropdown testId={`${testId}-enrolled`} />
                      ) : null}
                    </div>
                  )}
                </div>
                <hr className="modal-slot-separate" id="modal-slot-hr-title" />
                <div className="slots-button-container">
                  <div />
                  <div className="center-button-container">
                    <Button
                      disabled={
                        !SlotsConstants.isCancellableSlot(slot.status.tag)
                      }
                      onClick={() => deleteSlotConfirm(slot.id)}
                      type="button"
                      testId={`${testId}-delete-button`}
                      label={t("global:delete")}
                      width="220"
                      hierarchy="secondary"
                    />
                    <Button
                      type="button"
                      width="220"
                      hierarchy="primary"
                      disabled={invalid || !dirty}
                      onClick={() =>
                        onSubmit({
                          ...values,
                          refresh: () => form.mutators.forceValidation(),
                        })
                      }
                      testId={`${testId}-edit-button`}
                      label={t("global:edit")}
                    />
                  </div>
                  <Button
                    label={t("global:update")}
                    type="button"
                    hierarchy="secondary"
                    iconName="refresh"
                    onClick={onRefresh}
                    disabled={
                      (newPickerDirty && !pickerErrorCode) ||
                      (dirty && !invalid)
                    }
                    testId={`${testId}-refresh-button`}
                    width="220"
                  />
                </div>
              </form>
            );
          }}
        </Form>
      </div>
    </Modal>
  );
};
